import { useCreateSupportTicketMutation } from '#/api/api.portal';
import mixins from '#/app/styles';
import Button from '#/components/Button/Button';
import { InputWithLabel } from '#/components/InputWIthLabel/InputWIthLabel';
import Select from '#/components/Select/Select';
import { UploadAttachments } from '#/components/UploadAttachments/UploadAttachments';
import { useAppNavigation } from '#/navigation/navigation.ref';
import { useToasterContext } from '#/toaster/toaster.provider';
import { useState } from 'react';
import { StyleSheet } from 'react-native';
import styles from './Support.module.scss';

const options = [
  { name: 'Sales', value: 'Sales', options: [] },
  {
    name: 'Tech support',
    value: 'Tech support',
    options: [
      { name: 'Desktop app', value: 'Desktop app', options: [] },
      { name: 'Web app', value: 'Web app', options: [] },
      { name: 'Mobile app', value: 'Mobile app', options: [] },
    ],
  },
  { name: 'Accounting', value: 'Accounting', options: [] },
  {
    name: 'Feature request',
    value: 'Feature request',
    options: [
      { name: 'Desktop app', value: 'Desktop app', options: [] },
      { name: 'Web app', value: 'Web app', options: [] },
      { name: 'Mobile app', value: 'Mobile app', options: [] },
    ],
  },
  { name: 'Other', value: 'Other', options: [] },
];

const defaultFormValues = {
  type: 'Other',
  product: 'Desktop app',
  subject: 'Test ticket from app',
  description: 'Nothin here',
  attachments: [] as File[],
};

const stylesheet = StyleSheet.create({
  select: {
    backgroundColor: mixins().color.darker,
    borderColor: mixins().color.grayDarkest,
    borderWidth: 1,
    borderBottomStyle: 'solid',
    height: 40,
    paddingHorizontal: 20,
    width: '100%',
  },
});

export const NewTicket = () => {
  const navigation = useAppNavigation();
  const [values, setValues] = useState(defaultFormValues);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { success } = useToasterContext();
  const { mutateAsync, isPending } = useCreateSupportTicketMutation();

  const handleChange =
    (name: keyof typeof defaultFormValues) => (value: (typeof defaultFormValues)[typeof name]) => {
      setValues(prev => ({ ...prev, [name]: value }));
    };

  const productOptions = options.find(opt => opt.value === values.type)?.options || [];

  const handleSubmit = async () => {
    setHasSubmitted(true);

    if (
      !values.type ||
      !values.subject ||
      !values.description ||
      (productOptions.length > 0 && !values.product)
    ) {
      return;
    }

    const formData = new FormData();

    formData.append('type', values.type);
    if (productOptions.length > 0) {
      formData.append('product', values.product);
    }
    formData.append('subject', values.subject);
    formData.append('description', values.description);
    values.attachments.forEach((file, index) => {
      formData.append(`attachments[${index}]`, file);
    });

    const response = await mutateAsync(formData);

    if (response.data.ticketId) {
      navigation.navigate('Support', { ticket: String(response.data.ticketId) });
      success({
        title: 'Your request has been submitted successfully.',
        message: 'Our support team will get back to you as soon as possible.',
      });
    }
  };

  const fieldRequired = (field: keyof typeof defaultFormValues) => {
    const tip = hasSubmitted && !values[field] ? 'This field is required' : undefined;

    return { tip, tipError: !!tip };
  };

  return (
    <div className={styles.form}>
      <InputWithLabel name="Type" {...fieldRequired('type')}>
        <Select
          style={stylesheet.select}
          values={options.map(({ name, value }) => ({ label: name, value }))}
          handleChange={handleChange('type')}
          value={values.type}
        />
      </InputWithLabel>
      {productOptions.length > 0 && (
        <InputWithLabel name="Product" {...fieldRequired('product')}>
          <Select
            value={values.product}
            style={stylesheet.select}
            values={productOptions.map(({ name, value }) => ({ label: name, value }))}
            handleChange={handleChange('product')}
          />
        </InputWithLabel>
      )}
      <InputWithLabel
        name="Subject"
        value={values.subject}
        onChange={handleChange('subject')}
        {...fieldRequired('subject')}
      />
      <InputWithLabel
        name="Description"
        value={values.description}
        onChange={handleChange('description')}
        multiline
        {...fieldRequired('description')}
      />
      <InputWithLabel name="Attachments">
        <UploadAttachments value={values.attachments} onChange={handleChange('attachments')} />
      </InputWithLabel>
      <div className={styles.formSubmitContainer}>
        <Button loading={isPending} onPress={handleSubmit}>
          Submit ticket
        </Button>
      </div>
    </div>
  );
};
